import { useState, useRef } from "react";
import { DEFAULT_ZOOM, ZOOM_LIMITS } from "@/map/utils/mapConstants";
import type { LatLng } from "@/map/types/map";

interface UseMapStateProps {
  overlayData?: {
    data?: {
      available: boolean;
      config?: {
        bounds: [[number, number], [number, number]];
      };
    };
  };
}

export const useMapState = ({ overlayData }: UseMapStateProps) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapsRef = useRef<typeof google.maps | null>(null);
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_ZOOM);
  const [isInBounds, setIsInBounds] = useState(true);

  /**
   * Handle zoom change
   */
  const handleZoomChanged = (map: google.maps.Map) => {
    const zoom = map.getZoom();
    if (zoom) {
      setCurrentZoom(zoom);
    }
  };

  /**
   * Handle bounds change for overlay intersection check
   */
  const handleBoundsChanged = (map: google.maps.Map) => {
    if (overlayData?.data?.available && overlayData.data.config) {
      const mapBounds = map.getBounds();
      const overlayBounds = overlayData.data.config.bounds;

      if (mapBounds && overlayBounds) {
        const sw = new google.maps.LatLng(overlayBounds[0][0], overlayBounds[0][1]);
        const ne = new google.maps.LatLng(overlayBounds[1][0], overlayBounds[1][1]);
        const overlay = new google.maps.LatLngBounds(sw, ne);
        setIsInBounds(mapBounds.intersects(overlay));
      }
    }
  };

  /**
   * Center map on specific coordinates. With `zoom`, also zooms toward the
   * point — but only ever *in* (never below the current level), so focusing a
   * marker that's already close doesn't zoom the map out.
   */
  const centerOn = (lat: number, lng: number, zoom?: number) => {
    const map = mapRef.current;
    if (!map) return;
    map.panTo({ lat, lng });
    if (zoom != null) {
      const current = map.getZoom() ?? 0;
      if (zoom > current) map.setZoom(zoom);
    }
  };

  /** Pan to a point without touching the zoom level. */
  const panTo = (lat: number, lng: number) => {
    mapRef.current?.panTo({ lat, lng });
  };

  /**
   * Whether a point is inside the current viewport bounds. Returns `true` when
   * bounds aren't available yet (best-effort — an unknown viewport counts as
   * "in view" so soft focus doesn't force a re-zoom).
   */
  const isInView = (lat: number, lng: number) => {
    const bounds = mapRef.current?.getBounds();
    if (!bounds) return true;
    return bounds.contains(new google.maps.LatLng(lat, lng));
  };

  /**
   * Frame every point. Unlike `centerOn` this may zoom OUT — it's for changing
   * the viewed subject (e.g. switching location), where the previous camera is
   * no longer relevant. Capped at {@link ZOOM_LIMITS.MAX_ZOOM} so a tight
   * cluster doesn't land at street level, matching the initial fit in
   * `useMapInit`.
   */
  const fitTo = (points: LatLng[]) => {
    const map = mapRef.current;
    if (!map || points.length === 0) return;
    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(ZOOM_LIMITS.SINGLE_POINT);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds);
    google.maps.event.addListenerOnce(map, "bounds_changed", () => {
      const zoom = map.getZoom();
      if (zoom && zoom > ZOOM_LIMITS.MAX_ZOOM) map.setZoom(ZOOM_LIMITS.MAX_ZOOM);
    });
  };

  return {
    mapRef,
    mapsRef,
    currentZoom,
    isInBounds,
    handleZoomChanged,
    handleBoundsChanged,
    centerOn,
    panTo,
    isInView,
    fitTo,
  };
};
